/**
 * Zustand store for Clipper app state
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Auth store - using localStorage for persistence across tabs and sessions
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setToken: (token) => set({ token }),
      login: (user, token) => set({ user, token, isAuthenticated: true }),
      logout: () => set({ user: null, token: null, isAuthenticated: false }),
      
      // Refresh user data from backend (called after email verification, login, etc.)
      refreshUser: async () => {
        const state = get();
        if (!state.token) return null;
        
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${state.token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            set({ user: data, isAuthenticated: true });
            return data;
          }
        } catch (error) {
          console.error('Failed to refresh user:', error);
        }
        return null;
      },
    }),
    {
      name: 'clipper-auth',
      storage: createJSONStorage(() => localStorage), // Changed from sessionStorage for better persistence
    }
  )
);

// Session store (court session) - localStorage for cross-tab persistence
export const useSessionStore = create(
  persist(
    (set) => ({
      activeSession: null,
      club: null,
      court: null,
      expiresAt: null,
      
      setSession: (session) => set({
        activeSession: session,
        club: session?.club || null,
        court: session?.court || null,
        expiresAt: session?.expires_at || null,
      }),
      
      clearSession: () => set({
        activeSession: null,
        club: null,
        court: null,
        expiresAt: null,
      }),
    }),
    {
      name: 'clipper-session',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// Location store - now persisted to remember permissions
export const useLocationStore = create(
  persist(
    (set) => ({
      userLocation: null,
      locationEnabled: false,
      locationError: null,
      permissionGranted: false, // Track if user has ever granted permission
      
      setUserLocation: (location) => set({ 
        userLocation: location, 
        locationEnabled: true,
        locationError: null,
        permissionGranted: true 
      }),
      setLocationError: (error) => set({ 
        locationError: error, 
        locationEnabled: false 
      }),
      setPermissionGranted: (granted) => set({ permissionGranted: granted }),
      clearLocation: () => set({ 
        userLocation: null, 
        locationEnabled: false, 
        locationError: null 
      }),
    }),
    {
      name: 'clipper-location',
      partialize: (state) => ({
        permissionGranted: state.permissionGranted,
        locationEnabled: state.locationEnabled
      })
    }
  )
);

// Permissions store - tracks browser permissions granted by user
export const usePermissionsStore = create(
  persist(
    (set, get) => ({
      cameraGranted: false,
      locationGranted: false,
      notificationsGranted: false,
      lastCameraCheck: null,
      lastLocationCheck: null,
      
      setCameraGranted: (granted) => {
        console.log('[Permissions Store] Setting cameraGranted:', granted);
        set({ 
          cameraGranted: granted, 
          lastCameraCheck: Date.now() 
        });
      },
      setLocationGranted: (granted) => {
        console.log('[Permissions Store] Setting locationGranted:', granted);
        set({ 
          locationGranted: granted, 
          lastLocationCheck: Date.now() 
        });
      },
      setNotificationsGranted: (granted) => set({ notificationsGranted: granted }),
      
      // Check if we should prompt for permission (if not granted recently)
      shouldPromptCamera: () => {
        const state = get();
        // Don't prompt if already granted
        if (state.cameraGranted) return false;
        // Only prompt once per session if denied
        const thirtyMinutes = 30 * 60 * 1000;
        if (state.lastCameraCheck && (Date.now() - state.lastCameraCheck) < thirtyMinutes) {
          return false;
        }
        return true;
      },
      
      shouldPromptLocation: () => {
        const state = get();
        if (state.locationGranted) return false;
        const thirtyMinutes = 30 * 60 * 1000;
        if (state.lastLocationCheck && (Date.now() - state.lastLocationCheck) < thirtyMinutes) {
          return false;
        }
        return true;
      },
      
      // Check actual browser permission status (call this outside of store actions)
      checkBrowserPermissions: async () => {
        try {
          // Check camera permission
          if (navigator.permissions) {
            try {
              const cameraStatus = await navigator.permissions.query({ name: 'camera' });
              if (cameraStatus.state === 'granted') {
                console.log('[Permissions Store] Browser camera permission: granted');
                set({ cameraGranted: true, lastCameraCheck: Date.now() });
              } else if (cameraStatus.state === 'denied') {
                console.log('[Permissions Store] Browser camera permission: denied');
                set({ cameraGranted: false, lastCameraCheck: Date.now() });
              }
            } catch (camErr) {
              console.log('[Permissions Store] Camera permission query not supported:', camErr.message);
            }
            
            try {
              // Check geolocation permission
              const geoStatus = await navigator.permissions.query({ name: 'geolocation' });
              if (geoStatus.state === 'granted') {
                console.log('[Permissions Store] Browser location permission: granted');
                set({ locationGranted: true, lastLocationCheck: Date.now() });
              } else if (geoStatus.state === 'denied') {
                console.log('[Permissions Store] Browser location permission: denied');
                set({ locationGranted: false, lastLocationCheck: Date.now() });
              }
            } catch (geoErr) {
              console.log('[Permissions Store] Geolocation permission query not supported:', geoErr.message);
            }
          }
        } catch (e) {
          // Permissions API not fully supported, ignore
          console.log('[Permissions Store] Permissions API check failed:', e.message);
        }
      },
      
      reset: () => set({
        cameraGranted: false,
        locationGranted: false,
        notificationsGranted: false,
        lastCameraCheck: null,
        lastLocationCheck: null
      })
    }),
    {
      name: 'clipper-permissions',
      // Only persist the granted flags, not timestamps (they can be regenerated)
      partialize: (state) => ({
        cameraGranted: state.cameraGranted,
        locationGranted: state.locationGranted,
        notificationsGranted: state.notificationsGranted
      })
    }
  )
);

// Clips store with caching
export const useClipsStore = create(
  persist(
    (set, get) => ({
      clips: [],
      lastFetch: null,  // Timestamp of last fetch
      sessionKey: null, // Current session key for cache invalidation
      pendingClip: null,
      isLoading: false,
      
      setClips: (clips, sessionKey) => set({ 
        clips, 
        lastFetch: Date.now(),
        sessionKey 
      }),
      
      addClip: (clip) => set((state) => ({ 
        clips: [clip, ...state.clips] 
      })),
      
      updateClip: (clipId, updates) => set((state) => ({
        clips: state.clips.map((c) => 
          c.id === clipId ? { ...c, ...updates } : c
        )
      })),
      
      setPendingClip: (clip) => set({ pendingClip: clip }),
      clearPendingClip: () => set({ pendingClip: null }),
      setLoading: (isLoading) => set({ isLoading }),
      
      // Check if cache is valid (less than 30 seconds old and same session)
      isCacheValid: (currentSessionKey) => {
        const state = get();
        const cacheAge = Date.now() - (state.lastFetch || 0);
        const sameSession = state.sessionKey === currentSessionKey;
        return sameSession && cacheAge < 30000; // 30 seconds cache
      },
      
      // Clear cache when session changes
      clearCache: () => set({ 
        clips: [], 
        lastFetch: null, 
        sessionKey: null 
      }),
    }),
    {
      name: 'clipper-clips',
      partialize: (state) => ({ 
        clips: state.clips,
        lastFetch: state.lastFetch,
        sessionKey: state.sessionKey
      }),
    }
  )
);

// UI store
export const useUIStore = create((set) => ({
  isLoading: false,
  loadingMessage: '',
  
  setLoading: (isLoading, message = '') => set({ 
    isLoading, 
    loadingMessage: message 
  }),
}));

// Onboarding store
export const useOnboardingStore = create(
  persist(
    (set) => ({
      step: 'login', // login | signup | verify-email | verify-sms | geolocation | qr-scan | confirm
      pendingUserId: null,
      
      setStep: (step) => set({ step }),
      setPendingUserId: (userId) => set({ pendingUserId: userId }),
      reset: () => set({ step: 'login', pendingUserId: null }),
    }),
    {
      name: 'clipper-onboarding',
    }
  )
);
